import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Button, TextField,
  CircularProgress, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Select, MenuItem, FormControl,
  InputLabel, Tooltip, Snackbar, Divider,
} from '@mui/material';
import CheckCircleIcon    from '@mui/icons-material/CheckCircle';
import CancelIcon         from '@mui/icons-material/Cancel';
import RefreshIcon        from '@mui/icons-material/Refresh';
import LockIcon           from '@mui/icons-material/Lock';
import PersonIcon         from '@mui/icons-material/Person';
import ArrowBackIcon      from '@mui/icons-material/ArrowBack';
import ReplayIcon         from '@mui/icons-material/Replay';
import EventBusyIcon      from '@mui/icons-material/EventBusy';

import {
  login, logout, isLogged, isAdmin, getUser,
  getAfiliacionesPendientes, aprobarAfiliacion, rechazarAfiliacion,
  reevaluarAfiliacion, eliminarSesionOrientacion,
} from '../services/authApi';

// ── Helpers ───────────────────────────────────────────────────────────────
const ESTADO_CHIPS = {
  PENDIENTE:  { label: 'Pendiente',  color: 'warning' },
  ACTIVO:     { label: 'Activo',     color: 'success' },
  RECHAZADO:  { label: 'Rechazado',  color: 'error'   },
  SUSPENDIDO: { label: 'Suspendido', color: 'default' },
};

function fmt(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('es-CO', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

// ═════════════════════════════════════════════════════════════════════════
// Login Form
// ═════════════════════════════════════════════════════════════════════════
function LoginPanel({ onLogin }) {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await login(email, password);
      onLogin();
    } catch (err) {
      setError(err?.data?.mensaje || 'Credenciales inválidas.');
    } finally { setLoading(false); }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg,#1a4d2e 0%,#2e7d32 60%,#43a047 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Paper elevation={4} sx={{ maxWidth: 400, width: '100%', borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ background: 'linear-gradient(135deg,#1a4d2e,#2e7d32)', px: 4, py: 3, textAlign: 'center' }}>
          <LockIcon sx={{ color: '#c8a84b', fontSize: 40, mb: 1 }} />
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>Panel Administrativo</Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>FENAGRO APL — Economías Colaborativas</Typography>
        </Box>
        <Box component="form" onSubmit={handleLogin} sx={{ p: 4 }}>
          <TextField fullWidth label="Correo electrónico" type="email" size="small" sx={{ mb: 2 }}
            value={email} onChange={e => setEmail(e.target.value)} required />
          <TextField fullWidth label="Contraseña" type="password" size="small" sx={{ mb: 3 }}
            value={password} onChange={e => setPassword(e.target.value)} required />
          {error && <Alert severity="error" sx={{ mb: 2, fontSize: '0.82rem' }}>{error}</Alert>}
          <Button fullWidth type="submit" variant="contained" disabled={loading}
            endIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ py: 1.2, background: 'linear-gradient(135deg,#1a4d2e,#43a047)', borderRadius: 2 }}>
            {loading ? 'Ingresando…' : 'Ingresar'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Panel principal
// ═════════════════════════════════════════════════════════════════════════
export default function AdminPanel() {
  const [logged, setLogged]       = useState(isLogged() && isAdmin());
  const [afiliados, setAfiliados] = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [estado, setEstado]       = useState('PENDIENTE');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [snack, setSnack]         = useState({ open: false, msg: '', severity: 'success' });

  // Diálogo de rechazo
  const [rechazarDialog, setRechazarDialog] = useState({ open: false, id: null, motivo: '' });
  // Diálogo de reevaluar
  const [reevaluarDialog, setReevaluarDialog] = useState({ open: false, id: null, nombre: '', motivo: '' });
  // Diálogo de eliminar sesión
  const [eliminarSesionDialog, setEliminarSesionDialog] = useState({ open: false, id: null, nombre: '', motivo: '' });
  // Diálogo detalle
  const [detalleDialog, setDetalleDialog]   = useState({ open: false, item: null });

  const PAGE_SIZE = 15;
  const user = getUser();

  const cargar = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const data = await getAfiliacionesPendientes(estado, page, PAGE_SIZE);
      setAfiliados(data.data || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err?.data?.mensaje || 'Error cargando datos.');
    } finally { setLoading(false); }
  }, [estado, page]);

  useEffect(() => { if (logged) cargar(); }, [logged, cargar]);

  const showSnack = (msg, severity = 'success') =>
    setSnack({ open: true, msg, severity });

  const handleAprobar = async (id, nombre) => {
    try {
      await aprobarAfiliacion(id);
      showSnack(`✅ ${nombre} aprobado exitosamente.`);
      cargar();
    } catch (err) {
      showSnack(err?.data?.mensaje || 'Error al aprobar.', 'error');
    }
  };

  const handleRechazar = async () => {
    const { id } = rechazarDialog;
    try {
      await rechazarAfiliacion(id, rechazarDialog.motivo);
      showSnack('Solicitud rechazada.');
      setRechazarDialog({ open: false, id: null, motivo: '' });
      cargar();
    } catch (err) {
      showSnack(err?.data?.mensaje || 'Error al rechazar.', 'error');
    }
  };

  const handleReevaluar = async () => {
    const { id, nombre } = reevaluarDialog;
    try {
      await reevaluarAfiliacion(id, reevaluarDialog.motivo);
      showSnack(`Solicitud de ${nombre} reactivada para evaluacion.`);
      setReevaluarDialog({ open: false, id: null, nombre: '', motivo: '' });
      cargar();
    } catch (err) {
      showSnack(err?.data?.mensaje || 'Error al reactivar.', 'error');
    }
  };

  const handleEliminarSesion = async () => {
    const { id, nombre } = eliminarSesionDialog;
    try {
      await eliminarSesionOrientacion(id, eliminarSesionDialog.motivo);
      showSnack(`Sesion de ${nombre} eliminada. Puede volver a agendar.`);
      setEliminarSesionDialog({ open: false, id: null, nombre: '', motivo: '' });
      cargar();
    } catch (err) {
      showSnack(err?.data?.mensaje || 'Error al eliminar sesion.', 'error');
    }
  };

  const handleLogout = () => { logout(); setLogged(false); };

  if (!logged) return <LoginPanel onLogin={() => setLogged(isLogged() && isAdmin())} />;

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <Box sx={{ minHeight: '100vh', background: '#f5f7f5' }}>
      {/* Header */}
      <Box sx={{ background: 'linear-gradient(135deg,#1a4d2e,#2e7d32)', px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton href="/" size="small" sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Box>
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem' }}>Panel Admin — FENAGRO APL</Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
              <PersonIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
              {user?.nombre} {user?.apellido} · {user?.roles?.join(', ')}
            </Typography>
          </Box>
        </Box>
        <Button size="small" onClick={handleLogout} sx={{ color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.3)', '&:hover': { color: '#fff', borderColor: '#fff' } }} variant="outlined">
          Cerrar sesión
        </Button>
      </Box>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Controles */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a3a1a', flexGrow: 1 }}>
            Solicitudes de Afiliación
            <Chip label={total} size="small" color="primary" sx={{ ml: 1.5, fontWeight: 700 }} />
          </Typography>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Filtrar estado</InputLabel>
            <Select value={estado} label="Filtrar estado" onChange={e => { setEstado(e.target.value); setPage(1); }}>
              <MenuItem value="PENDIENTE">Pendientes</MenuItem>
              <MenuItem value="ACTIVO">Activos</MenuItem>
              <MenuItem value="RECHAZADO">Rechazados</MenuItem>
              <MenuItem value="">Todos</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Actualizar">
            <IconButton onClick={cargar} disabled={loading} sx={{ background: '#fff', border: '1px solid #e0e0e0' }}>
              {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Tabla */}
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: '#f8fdf8' }}>
                {['#', 'Nombre completo', 'Cédula', 'Email', 'Municipio', 'Modalidad', 'Fecha solicitud', 'Estado', 'Acciones'].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#2e7d32', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {afiliados.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={9} sx={{ textAlign: 'center', py: 5, color: '#888' }}>
                    No hay solicitudes con el estado seleccionado.
                  </TableCell>
                </TableRow>
              )}
              {afiliados.map((a) => {
                const chip = ESTADO_CHIPS[a.estado] || { label: a.estado, color: 'default' };
                return (
                  <TableRow key={a.afiliadoID} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell sx={{ fontSize: '0.78rem', color: '#888' }}>#{a.afiliadoID}</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                      <Button size="small" onClick={() => setDetalleDialog({ open: true, item: a })}
                        sx={{ textTransform: 'none', fontWeight: 600, color: '#1a4d2e', p: 0, minWidth: 0, '&:hover': { textDecoration: 'underline', background: 'none' } }}>
                        {a.nombres} {a.apellidos}
                      </Button>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', fontFamily: 'monospace' }}>{a.documento}</TableCell>
                    <TableCell sx={{ fontSize: '0.76rem', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Tooltip title={a.email || ''}><span>{a.email || '—'}</span></Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.76rem', whiteSpace: 'nowrap' }}>
                      {[a.municipio, a.departamento].filter(Boolean).join(', ') || '—'}
                    </TableCell>
                    <TableCell>
                      <Chip label={a.modalidadAporte || '—'} size="small" variant="outlined"
                        sx={{ fontSize: '0.68rem', height: 20, borderColor: '#c8e6c9', color: '#2e7d32' }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.76rem', whiteSpace: 'nowrap' }}>{fmt(a.fechaCreacion)}</TableCell>
                    <TableCell>
                      <Chip label={chip.label} color={chip.color} size="small" sx={{ fontSize: '0.7rem', fontWeight: 700 }} />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {a.estado === 'PENDIENTE' && (
                        <>
                          <Tooltip title="Aprobar">
                            <IconButton size="small" color="success"
                              onClick={() => handleAprobar(a.afiliadoID, `${a.nombres} ${a.apellidos}`)}>
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Rechazar">
                            <IconButton size="small" color="error"
                              onClick={() => setRechazarDialog({ open: true, id: a.afiliadoID, motivo: '' })}>
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {a.estado === 'RECHAZADO' && (
                        <Tooltip title="Reevaluar (reactivar solicitud)">
                          <IconButton size="small" sx={{ color: '#e65100' }}
                            onClick={() => setReevaluarDialog({ open: true, id: a.afiliadoID, nombre: `${a.nombres} ${a.apellidos}`, motivo: '' })}>
                            <ReplayIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {a.tieneSesion && (
                        <Tooltip title="Eliminar sesion de orientacion">
                          <IconButton size="small" sx={{ color: '#6a1b9a' }}
                            onClick={() => setEliminarSesionDialog({ open: true, id: a.afiliadoID, nombre: `${a.nombres} ${a.apellidos}`, motivo: '' })}>
                            <EventBusyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Paginación simple */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 3 }}>
            <Button size="small" disabled={page === 1} onClick={() => setPage(p => p - 1)} variant="outlined">
              ← Anterior
            </Button>
            <Typography variant="body2" sx={{ alignSelf: 'center', color: '#666' }}>
              Página {page} de {totalPages}
            </Typography>
            <Button size="small" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} variant="outlined">
              Siguiente →
            </Button>
          </Box>
        )}
      </Container>

      {/* Diálogo de detalle */}
      <Dialog open={detalleDialog.open} onClose={() => setDetalleDialog({ open: false, item: null })} maxWidth="sm" fullWidth>
        {detalleDialog.item && (() => {
          const a = detalleDialog.item;
          const chip = ESTADO_CHIPS[a.estado] || { label: a.estado, color: 'default' };
          return (
            <>
              <DialogTitle sx={{ background: '#f8fdf8', borderBottom: '1px solid #e0e0e0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontWeight: 700, color: '#1a3a1a' }}>
                    Detalle — #{a.afiliadoID}
                  </Typography>
                  <Chip label={chip.label} color={chip.color} size="small" sx={{ fontWeight: 700 }} />
                </Box>
              </DialogTitle>
              <DialogContent sx={{ pt: 3 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                  {[
                    ['Nombre', `${a.nombres} ${a.apellidos}`],
                    ['Cédula', a.documento],
                    ['Email', a.email],
                    ['Teléfono', a.telefono],
                    ['Ubicación', [a.municipio, a.departamento].filter(Boolean).join(', ')],
                    ['Modalidad aporte', a.modalidadAporte],
                    ['Valor afiliac. cooperativa', `$${(a.valorAfiliacionCooperativa || 60000).toLocaleString('es-CO')} COP`],
                    ['Aporte mensual', `$${(a.valorAporteMensual || 400000).toLocaleString('es-CO')} COP`],
                    ['Fecha afiliación', fmt(a.fechaAfiliacion)],
                    ['Fecha solicitud', fmt(a.fechaCreacion)],
                  ].map(([l, v]) => v && (
                    <Box key={l}>
                      <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.68rem' }}>{l}</Typography>
                      <Typography variant="body2" sx={{ color: '#1a1a1a', mt: 0.3 }}>{v || '—'}</Typography>
                    </Box>
                  ))}
                </Box>
              </DialogContent>
              <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #f0f0f0' }}>
                {a.estado === 'PENDIENTE' && (
                  <>
                    <Button color="error" startIcon={<CancelIcon />}
                      onClick={() => { setDetalleDialog({ open: false, item: null }); setRechazarDialog({ open: true, id: a.afiliadoID, motivo: '' }); }}>
                      Rechazar
                    </Button>
                    <Button variant="contained" color="success" startIcon={<CheckCircleIcon />}
                      onClick={() => { handleAprobar(a.afiliadoID, `${a.nombres} ${a.apellidos}`); setDetalleDialog({ open: false, item: null }); }}
                      sx={{ background: 'linear-gradient(135deg,#1a4d2e,#43a047)' }}>
                      Aprobar
                    </Button>
                  </>
                )}
                <Button onClick={() => setDetalleDialog({ open: false, item: null })}>Cerrar</Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* Diálogo de rechazo con motivo */}
      <Dialog open={rechazarDialog.open} onClose={() => setRechazarDialog({ open: false, id: null, motivo: '' })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: '#b71c1c', fontWeight: 700 }}>Rechazar solicitud</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
            Indique el motivo del rechazo (opcional). Se registrará en el historial.
          </Typography>
          <TextField fullWidth multiline rows={3} size="small" label="Motivo del rechazo"
            value={rechazarDialog.motivo}
            onChange={e => setRechazarDialog(p => ({ ...p, motivo: e.target.value }))} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRechazarDialog({ open: false, id: null, motivo: '' })}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleRechazar}>Confirmar rechazo</Button>
        </DialogActions>
      </Dialog>

      {/* Dialogo Reevaluar */}
      <Dialog open={reevaluarDialog.open} onClose={() => setReevaluarDialog({ open: false, id: null, nombre: '', motivo: '' })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: '#e65100', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReplayIcon /> Reevaluar solicitud
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
            La solicitud de <strong>{reevaluarDialog.nombre}</strong> volvera al estado <strong>EN EVALUACION</strong> (Paso 2). Indique el motivo (opcional).
          </Typography>
          <TextField fullWidth multiline rows={3} size="small" label="Motivo de reevaluacion"
            value={reevaluarDialog.motivo}
            onChange={e => setReevaluarDialog(p => ({ ...p, motivo: e.target.value }))} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setReevaluarDialog({ open: false, id: null, nombre: '', motivo: '' })}>Cancelar</Button>
          <Button variant="contained" sx={{ background: '#e65100' }} startIcon={<ReplayIcon />} onClick={handleReevaluar}>
            Confirmar reevaluacion
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogo Eliminar Sesion */}
      <Dialog open={eliminarSesionDialog.open} onClose={() => setEliminarSesionDialog({ open: false, id: null, nombre: '', motivo: '' })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: '#6a1b9a', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventBusyIcon /> Eliminar sesion de orientacion
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
            Se eliminara la sesion programada de <strong>{eliminarSesionDialog.nombre}</strong>. El afiliado podra volver a agendar una nueva sesion. Indique el motivo (opcional).
          </Typography>
          <TextField fullWidth multiline rows={3} size="small" label="Motivo"
            value={eliminarSesionDialog.motivo}
            onChange={e => setEliminarSesionDialog(p => ({ ...p, motivo: e.target.value }))} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEliminarSesionDialog({ open: false, id: null, nombre: '', motivo: '' })}>Cancelar</Button>
          <Button variant="contained" sx={{ background: '#6a1b9a' }} startIcon={<EventBusyIcon />} onClick={handleEliminarSesion}>
            Confirmar eliminacion
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} onClose={() => setSnack(p => ({ ...p, open: false }))} sx={{ fontWeight: 600 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
